import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useIsMobile } from '@/hooks/use-mobile';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, MapPin, Users, DollarSign, Clock, Award } from 'lucide-react';
import { format } from 'date-fns';

const formatTime = (time: string) => {
  if (!time) return '';
  try {
    const [h, m] = time.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour = h % 12 || 12;
    return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
  } catch { return time; }
};

const TrainingSessionDetail = () => {
  const { sessionId, username, slug } = useParams<{ sessionId?: string; username?: string; slug?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [session, setSession] = useState<any>(null);
  const [coachName, setCoachName] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (sessionId) {
      loadSessionById(sessionId);
    } else if (username && slug) {
      loadSessionBySlug(username, slug);
    }
  }, [sessionId, username, slug]);

  const fetchCoachName = async (coachId: string) => {
    const { data: coach } = await supabase
      .from('internal_users')
      .select('username')
      .eq('id', coachId)
      .single();
    if (coach) setCoachName(coach.username);
  };

  const loadSessionById = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('training_sessions')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      setSession(data);
      if (data?.coach_id) fetchCoachName(data.coach_id);
    } catch (error) {
      console.error('Error loading session:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSessionBySlug = async (uname: string, titleSlug: string) => {
    try {
      // Find coach by username
      const { data: coach } = await supabase
        .from('internal_users')
        .select('id, username')
        .eq('username', uname)
        .single();

      if (!coach) { setLoading(false); return; }
      setCoachName(coach.username);

      // Get all sessions by this coach and match slug
      const { data: sessions } = await supabase
        .from('training_sessions')
        .select('*')
        .eq('coach_id', coach.id);

      const match = sessions?.find(s => {
        const sSlug = s.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        return sSlug === titleSlug;
      });

      if (match) setSession(match);
    } catch (error) {
      console.error('Error loading session:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSessionTypeBadge = (type: string) => {
    const colors: Record<string, string> = {
      group_training: 'bg-blue-600/20 text-blue-500 border-blue-600/30',
      private_lesson: 'bg-purple-600/20 text-purple-500 border-purple-600/30',
      camp: 'bg-green-600/20 text-green-500 border-green-600/30',
      clinic: 'bg-orange-600/20 text-orange-500 border-orange-600/30',
    };
    return colors[type] || 'bg-gray-600/20 text-gray-400';
  };

  // Update URL to SEO-friendly format once data loads
  useEffect(() => {
    if (session && coachName) {
      const slug = session.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const seoPath = `/${coachName}/training-sessions/${slug}`;
      window.history.replaceState(null, '', seoPath);
    }
  }, [session, coachName]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-black">
        {!isMobile && <Header />}
        {isMobile && <MobileHeader />}
        <main className="flex-1 flex items-center justify-center">
          <p className="text-gray-400">Loading session...</p>
        </main>
        {!isMobile && <Footer />}
        {isMobile && <MobileBottomNav />}
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex flex-col bg-black">
        {!isMobile && <Header />}
        {isMobile && <MobileHeader />}
        <main className="flex-1 flex items-center justify-center flex-col gap-4">
          <p className="text-gray-400">Session not found</p>
          <Button variant="outline" onClick={() => navigate('/training-sessions')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Sessions
          </Button>
        </main>
        {!isMobile && <Footer />}
        {isMobile && <MobileBottomNav />}
      </div>
    );
  }

  const spotsLeft = session.max_participants
    ? session.max_participants - (session.current_participants || 0)
    : null;

  return (
    <div className="min-h-screen flex flex-col bg-black">
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}

      <main className={`flex-1 container mx-auto px-4 ${isMobile ? 'pt-20 pb-24' : 'mt-24 py-8'}`}>
        <Button
          variant="ghost"
          onClick={() => navigate('/training-sessions')}
          className="mb-6 text-gray-400 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Sessions
        </Button>

        <div className="max-w-3xl mx-auto space-y-6">
          {/* Header Card */}
          <Card className="bg-gray-900 border-gray-800">
            <CardHeader>
              <div className="flex flex-wrap justify-between items-start gap-3">
                <div>
                  <CardTitle className="text-2xl md:text-3xl text-white mb-2">{session.title}</CardTitle>
                  {coachName && (
                    <p className="text-sm text-gray-400">
                      Coach: <span className="text-sports-green font-medium">@{coachName}</span>
                    </p>
                  )}
                </div>
                <Badge className={`${getSessionTypeBadge(session.session_type)} text-sm`}>
                  {session.session_type.replace(/_/g, ' ')}
                </Badge>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Badge variant="outline" className="text-sports-green border-sports-green">
                  {session.sport}
                </Badge>
                {session.skill_level && (
                  <Badge variant="outline">{session.skill_level}</Badge>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {session.description && (
                <p className="text-gray-300 mb-6">{session.description}</p>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 text-gray-300">
                  <Calendar className="h-5 w-5 text-sports-green shrink-0" />
                  <span>{format(new Date(session.date), 'EEEE, MMMM dd, yyyy')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-sports-green shrink-0" />
                  <span className="text-lg font-semibold text-white">{formatTime(session.start_time)} – {formatTime(session.end_time)}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <MapPin className="h-5 w-5 text-sports-green shrink-0" />
                  <span>{session.location}{session.address ? `, ${session.address}` : ''}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-300">
                  <Users className="h-5 w-5 text-sports-green shrink-0" />
                  <span>
                    {session.current_participants || 0}/{session.max_participants || '∞'} participants
                    {spotsLeft !== null && spotsLeft > 0 && (
                      <span className="text-sports-green ml-1">({spotsLeft} spots left)</span>
                    )}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Price & Action Card */}
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <DollarSign className="h-6 w-6 text-sports-green" />
                <span className="text-3xl font-bold text-white">${session.price}</span>
                <span className="text-gray-400">per session</span>
              </div>
              {user && session.coach_id !== user.id && (
                <Button className="bg-sports-green text-black hover:bg-green-600 px-8">
                  Book Session
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Additional Details */}
          {(session.requirements || session.equipment_needed) && (
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Award className="h-5 w-5 text-sports-green" />
                  Additional Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {session.requirements && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-1">Requirements</h4>
                    <p className="text-gray-300">{session.requirements}</p>
                  </div>
                )}
                {session.equipment_needed && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-400 mb-1">Equipment Needed</h4>
                    <p className="text-gray-300">{session.equipment_needed}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {!isMobile && <Footer />}
      {isMobile && <MobileBottomNav />}
    </div>
  );
};

export default TrainingSessionDetail;
